import { XCloseIcon, XIcon } from '@langchain/untitled-ui-icons';

import { ToolDefinition } from 'node_modules/@langchain/core/dist/language_models/base';
import { useState } from 'react';

import { PromptSelector } from '@/Pages/Prompts/components/PromptSelector';
import { getPromptDisplayNameForSelector } from '@/Pages/Prompts/utils/Prompts.utils';
import { Tooltip } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { CommitWithLookupsSchema, RepoWithLookupsSchema } from '@/types/schema';

import { OptimizePrompt } from '../Optimization/OptimizePrompt';
import { PlaygroundPromptType } from '../PlaygroundContext';
import { PlaygroundIcon } from '../PlaygroundHome/PlaygroundButtons';
import { PlaygroundHomeSave } from '../PlaygroundHome/PlaygroundHomeSave';
import { useDefaultPromptConfig } from '../PlaygroundHome/hooks/useDefaultPromptConfig';
import {
  useDeep,
  usePlaygroundStore,
} from '../PlaygroundHome/hooks/usePlaygroundStore';
import { PlaygroundPromptCommit } from '../PlaygroundPrompt/PlaygroundPrompt';
import { PromptSettings } from '../components/PromptSettings';
import { usePlaygroundParentContext } from '../context/PlaygroundParentContext';
import {
  createExperimentName,
  getDefaultModelManifest,
  storedManifestToPlaygroundManifestAndOptions,
} from '../utils/Playground.utils';
import { ChevronToggle } from './ChevronToggle';

export const PlaygroundPromptTopBar = ({
  index,
  isAllCollapsed,
  setIsAllCollapsed,
}: {
  index: number;
  isAllCollapsed: boolean;
  setIsAllCollapsed: () => void;
}) => {
  const { isSimplified } = usePlaygroundParentContext();
  const prompt = usePlaygroundStore(useDeep((state) => state.prompts[index]));
  const columnId = usePlaygroundStore((state) => state.prompts[index].columnId);
  const promptIsEdited = usePlaygroundStore(
    (state) => state.prompts[index].hubPrompt?.edited
  );
  const setPrompt = usePlaygroundStore((state) => state.setPrompt);
  const removePrompt = usePlaygroundStore((state) => state.removePrompt);
  const promptCount = usePlaygroundStore((state) => state.prompts.length);
  const resetPrompt = usePlaygroundStore((state) => state.resetPrompt);
  const setEdited = usePlaygroundStore((state) => state.setEdited);

  const { hubPrompt, manifest, options } = prompt;
  const isSavedPrompt = !!hubPrompt;
  const isRun = !!prompt.runs?.length;

  const { defaultConfig, defaultConfigId } = useDefaultPromptConfig();
  // todo: store config id for saved prompts
  const [storedConfigIdToLoad, setStoredConfigIdToLoad] = useState<
    string | undefined
  >(isSavedPrompt || isRun ? undefined : defaultConfigId);

  const { value: promptOptimizationJobsEnabled } = useOrgConfig(
    OrgConfigs.prompt_optimization_jobs_enabled
  );

  const onSelectPrompt = (
    selectedPrompt: RepoWithLookupsSchema,
    selectedCommit?: CommitWithLookupsSchema
  ) => {
    setStoredConfigIdToLoad(undefined);
    setPrompt(index, (prev) => {
      // get the manifest from the latest commit, if it doesn't exist, create a default manifest
      const storedManifest =
        selectedCommit?.manifest ??
        selectedPrompt.latest_commit_manifest?.manifest;
      if (!storedManifest) return prev;
      const { promptManifest, modelManifest, options } =
        storedManifestToPlaygroundManifestAndOptions(storedManifest);

      const newPrompt: PlaygroundPromptType = {
        ...prev,
        hubPrompt: {
          prompt: selectedPrompt,
          edited: false,
          commitHash:
            selectedCommit?.commit_hash ??
            selectedPrompt.last_commit_hash ??
            undefined,
        },
        manifest: {
          ...prev.manifest,
          kwargs: {
            ...prev.manifest.kwargs,
            first: promptManifest ?? prev.manifest.kwargs.first,
            last: modelManifest ?? prev.manifest.kwargs.last,
          },
        },
        options,
        customExperimentName: createExperimentName({
          repoHandle: selectedPrompt.repo_handle,
          modelManifest: modelManifest ?? prev.manifest.kwargs.last,
        }),
      };
      return newPrompt;
    });
  };

  const renderPromptSelector = () => {
    if (isSimplified) return null;
    return (
      <div className="flex min-w-[50px] shrink-0 flex-row gap-1">
        <PromptSelector
          selectedPromptIdentifier={
            hubPrompt?.commitHash
              ? `${getPromptDisplayNameForSelector(
                  hubPrompt?.prompt
                )}:${hubPrompt?.commitHash?.slice(0, 8)}`
              : undefined
          }
          prompt={hubPrompt?.prompt}
          selectByLatest={true}
          showCommitSelector={true}
          onChange={onSelectPrompt}
          fullInfo
          className="min-w-[50px] max-w-[200px] flex-shrink text-sm"
        />
        {!!hubPrompt?.prompt && (
          <Tooltip title="Unselect prompt" side="right">
            <button
              type="button"
              className="my-1 flex-shrink-0 rounded-md p-1 text-secondary file:text-sm hover:bg-tertiary"
              onClick={() =>
                resetPrompt(
                  index,
                  defaultConfig
                    ? getDefaultModelManifest(defaultConfig.settings)
                    : undefined
                )
              }
            >
              <XIcon className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderPromptSaveButton = () => {
    if (isSimplified) return null;
    if (hubPrompt) {
      return (
        <PlaygroundPromptCommit
          manifest={manifest}
          disabled={!promptIsEdited}
          repo={hubPrompt.prompt}
          onSuccess={(newCommitHash) => {
            setPrompt(index, (prev) => ({
              ...prev,
              hubPrompt: {
                ...hubPrompt,
                prompt: {
                  ...hubPrompt.prompt,
                  last_commit_hash: newCommitHash,
                },
                commitHash: newCommitHash,
              },
            }));
            setEdited(index, false);
          }}
          replaceTools={options.tools as ToolDefinition[]}
        />
      );
    }
    return (
      <PlaygroundHomeSave
        manifest={manifest}
        examples={[]}
        disabled={false}
        replaceTools={options.tools as ToolDefinition[]}
        onSuccess={(
          repoWithLookups: RepoWithLookupsSchema,
          commit: CommitWithLookupsSchema
        ) => {
          onSelectPrompt(repoWithLookups, commit);
        }}
      />
    );
  };

  const renderPromptOptimization = () => {
    if (isSimplified || !promptOptimizationJobsEnabled) return null;
    return (
      <OptimizePrompt
        initialPromptIdentifier={hubPrompt?.prompt.repo_handle}
        disabledMessage={
          !hubPrompt?.prompt ? 'Save prompt to optimize' : undefined
        }
      />
    );
  };

  return (
    <div className="flex w-full shrink-0 gap-2 pl-4">
      {renderPromptSelector()}
      {!isSimplified && (
        <div className="my-auto h-px flex-grow bg-[#EAECF0] dark:bg-[#1F242F]" />
      )}
      <div className="ml-auto flex items-center gap-1 overflow-x-hidden no-scrollbar">
        {renderPromptOptimization()}
        <PromptSettings
          index={index}
          storedConfigIdToLoad={storedConfigIdToLoad}
          setStoredConfigIdToLoad={setStoredConfigIdToLoad}
        />
        {renderPromptSaveButton()}
        {promptCount > 1 && (
          <PlaygroundIcon className="my-1" tooltipTitle="Remove comparison">
            <button
              type="button"
              onClick={() => removePrompt(columnId)}
              className="mx-0.5 my-1"
            >
              <XCloseIcon className="h-4 w-4" />
            </button>
          </PlaygroundIcon>
        )}
      </div>
      <ChevronToggle
        isAllCollapsed={isAllCollapsed}
        setIsAllCollapsed={setIsAllCollapsed}
      />
    </div>
  );
};
export default PlaygroundPromptTopBar;
